import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Users.css';

const API = '/api/v2';

function Users() {
  const [users, setUsers] = useState([]);
  const [centers, setCenters] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showAddCenter, setShowAddCenter] = useState(false);
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'surgeon', center_id: '' });
  const [showPw, setShowPw] = useState(false);
  const [centerForm, setCenterForm] = useState({ name: '', city: '', country: 'France', type: 'hospital' });
  const [cohortInput, setCohortInput] = useState('');
  const [existingCohorts, setExistingCohorts] = useState([]);

  const loadCohorts = () => {
    axios.get('/api/admin/cohorts').then(r => setExistingCohorts(r.data.cohorts || [])).catch(()=>{});
  };

  const addCohort = async (userId, cohort) => {
    const clean = cohort.trim().toUpperCase();
    try {
      await axios.post('/api/admin/users/' + userId + '/cohorts', { cohort: clean });
      setCohortInput('');
      flash('Added to ' + clean);
      load(); loadCohorts();
    } catch(e) { flash(e.response?.data?.error || 'Error'); }
  };

  const removeCohort = async (userId, cohort) => {
    try {
      await axios.delete('/api/admin/users/' + userId + '/cohorts/' + cohort);
      flash('Removed from ' + cohort);
      load(); loadCohorts();
    } catch(e) { flash(e.response?.data?.error || 'Error'); }
  };

  const load = () => {
    axios.get(API + '/users').then(r => setUsers(r.data.users || []));
    axios.get(API + '/centers').then(r => setCenters(r.data.centers || []));
  };
  useEffect(() => { load(); loadCohorts(); }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const addUser = async () => {
    try {
      await axios.post(API + '/users', { ...form, center_id: form.center_id || null });
      flash('User created');
      setShowAdd(false);
      setForm({ name: '', email: '', password: '', role: 'surgeon', center_id: '' });
      load();
    } catch (e) { flash(e.response?.data?.error || 'Error'); }
  };

  const updateUser = async (id, data) => {
    try {
      await axios.put(API + '/users/' + id, data);
      flash('User updated');
      setEditId(null);
      load();
    } catch (e) { flash(e.response?.data?.error || 'Error'); }
  };

  const resetPw = async (id) => {
    const pw = prompt('New password (min 6 chars):');
    if (!pw) return;
    try {
      await axios.put(API + '/users/' + id + '/password', { password: pw });
      flash('Password reset');
    } catch (e) { flash(e.response?.data?.error || 'Error'); }
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try {
      await axios.delete(API + '/users/' + id);
      flash('User deleted');
      load();
    } catch (e) { flash(e.response?.data?.error || 'Error'); }
  };

  const addCenter = async () => {
    try {
      await axios.post(API + '/centers', centerForm);
      flash('Center created');
      setShowAddCenter(false);
      setCenterForm({ name: '', city: '', country: 'France', type: 'hospital' });
      load();
    } catch (e) { flash(e.response?.data?.error || 'Error'); }
  };

  const roleBadge = (role) => {
    const colors = { admin: '#dc2626', surgeon: '#2563eb', researcher: '#059669', center: '#d97706' };
    return <span className="u-badge" style={{background: colors[role] || '#64748b'}}>{role.toUpperCase()}</span>;
  };

  return (
    <div className="users-page">
      <div className="u-header">
        <h1>User Management</h1>
        <div className="u-header-actions">
          <button className="btn-add-center" onClick={() => setShowAddCenter(true)}>+ Add Center</button>
          <button className="btn-add" onClick={() => setShowAdd(true)}>+ Add User</button>
        </div>
      </div>

      {msg && <div className="u-flash">{msg}</div>}

      {/* Centers Section */}
      <div className="u-centers-section">
        <h2>Centers</h2>
        <div className="u-centers-grid">
          {centers.map(c => (
            <div key={c.id} className="u-center-card">
              <div className="uc-name">🏥 {c.name}</div>
              <div className="uc-meta">{c.city}, {c.country}</div>
              <div className="uc-stats">
                <span>{c.patient_count} patients</span>
                <span>{c.user_count} users</span>
              </div>
              {c.wallet_address && <div className="uc-wallet" title={c.wallet_address}>🔗 {c.wallet_address.slice(0,8)}...</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Users Table */}
      <table className="u-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Center</th>
            <th>Patients</th>
            <th>Status</th>
            <th>Last Login</th>
            <th>Cohorts</th><th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id}>
              <td className="u-name">{u.name}</td>
              <td className="u-email">{u.email}</td>
              <td>{editId === u.id ? (
                <select value={u.role} onChange={e => updateUser(u.id, { role: e.target.value })}>
                  <option value="surgeon">surgeon</option>
                  <option value="center">center</option>
                  <option value="admin">admin</option>
                  <option value="researcher">researcher</option>
                </select>
              ) : roleBadge(u.role)}</td>
              <td>{editId === u.id ? (
                <select value={u.center_id || ''} onChange={e => updateUser(u.id, { center_id: e.target.value || null })}>
                  <option value="">— No center —</option>
                  {centers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              ) : (
                <span className={u.center_name ? 'u-center-tag' : 'u-no-center'}>{u.center_name || '—'}</span>
              )}</td>
              <td className="u-count">{u.patient_count}</td>
              <td><span className={'u-status ' + (u.is_active !== false ? 'active' : 'inactive')}>{u.is_active !== false ? 'Active' : 'Inactive'}</span></td>
              <td className="u-date">{u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}</td>
              <td className="u-actions" style={{minWidth:'180px'}}>
                {editId === u.id && (
                  <div style={{marginBottom:'8px'}}>
                    <div style={{display:'flex',flexWrap:'wrap',gap:'4px',marginBottom:'6px'}}>
                      {(u.cohorts||[]).length===0 && <span style={{color:'#94a3b8',fontSize:'12px'}}>No cohorts</span>}
                      {(u.cohorts||[]).map(c => (
                        <span key={c} style={{display:'inline-flex',alignItems:'center',gap:'4px',background:'rgba(37,99,235,0.1)',color:'#3b82f6',border:'1px solid rgba(37,99,235,0.3)',borderRadius:'4px',padding:'2px 7px',fontSize:'11px',fontWeight:'600'}}>
                          {c}
                          <button onClick={()=>removeCohort(u.id,c)} style={{background:'none',border:'none',color:'#3b82f6',cursor:'pointer',fontSize:'13px',lineHeight:1,padding:0}}>×</button>
                        </span>
                      ))}
                    </div>
                    <div style={{display:'flex',gap:'4px'}}>
                      <input
                        list={'cohorts-'+u.id}
                        value={cohortInput}
                        onChange={e=>setCohortInput(e.target.value)}
                        onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();addCohort(u.id,cohortInput);}}}
                        placeholder="SRC, SFCR…"
                        style={{flex:1,padding:'3px 7px',fontSize:'12px',border:'1px solid #d1d5db',borderRadius:'4px'}}
                      />
                      <datalist id={'cohorts-'+u.id}>{existingCohorts.map(c=><option key={c} value={c}/>)}</datalist>
                      <button className="btn-sm" onClick={()=>addCohort(u.id,cohortInput)}>+</button>
                    </div>
                  </div>
                )}
                {editId !== u.id && (
                  <div style={{display:'flex',flexWrap:'wrap',gap:'4px'}}>
                    {(u.cohorts||[]).length===0 && <span style={{color:'#94a3b8',fontSize:'12px'}}>—</span>}
                    {(u.cohorts||[]).map(c=><span key={c} style={{background:'rgba(37,99,235,0.1)',color:'#3b82f6',borderRadius:'4px',padding:'2px 7px',fontSize:'11px',fontWeight:'600'}}>{c}</span>)}
                  </div>
                )}
              </td>
              <td className="u-actions">
                <button className="btn-sm" onClick={() => setEditId(editId === u.id ? null : u.id)}>{editId === u.id ? 'Done' : 'Edit'}</button>
                <button className="btn-sm warn" onClick={() => resetPw(u.id)}>Reset PW</button>
                <button className="btn-sm danger" onClick={() => deleteUser(u.id, u.name)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="u-footer">{users.length} users · {users.filter(u => u.role === 'surgeon').length} surgeons · {users.filter(u => u.role === 'center').length} centers · {users.filter(u => u.role === 'admin').length} admins · {users.filter(u => u.role === 'researcher').length} researchers</div>

      {/* Add User Modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Add User</h2>
            <div className="modal-form">
              <label>Name</label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Dr. John Smith" />
              <label>Email</label>
              <input value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="john@hospital.com" type="email" />
              <label>Password</label>
              <div className="pw-field">
                <input value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Min 6 characters" type={showPw ? 'text' : 'password'} />
                <button type="button" className="pw-toggle" onClick={() => setShowPw(!showPw)}>{showPw ? '🙈' : '👁'}</button>
              </div>
              <label>Role</label>
              <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                <option value="surgeon">Surgeon</option>
                <option value="center">Center / Institution</option>
                <option value="admin">Admin</option>
                <option value="researcher">Researcher</option>
              </select>
              <label>Center</label>
              <select value={form.center_id} onChange={e => setForm({...form, center_id: e.target.value})}>
                <option value="">— No center —</option>
                {centers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.city})</option>)}
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn-save" onClick={addUser}>Create User</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Center Modal */}
      {showAddCenter && (
        <div className="modal-overlay" onClick={() => setShowAddCenter(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Add Center</h2>
            <div className="modal-form">
              <label>Name</label>
              <input value={centerForm.name} onChange={e => setCenterForm({...centerForm, name: e.target.value})} placeholder="Hôpital Privé du Dos" />
              <label>City</label>
              <input value={centerForm.city} onChange={e => setCenterForm({...centerForm, city: e.target.value})} placeholder="Lyon" />
              <label>Country</label>
              <input value={centerForm.country} onChange={e => setCenterForm({...centerForm, country: e.target.value})} placeholder="France" />
              <label>Type</label>
              <select value={centerForm.type} onChange={e => setCenterForm({...centerForm, type: e.target.value})}>
                <option value="hospital">Hospital</option>
                <option value="clinic">Clinic</option>
                <option value="university">University</option>
                <option value="research">Research Center</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowAddCenter(false)}>Cancel</button>
              <button className="btn-save" onClick={addCenter}>Create Center</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
